import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';
import withHelperText from "../../hocs/with_helper_text.js";
import withAutoDeletion from "../../hocs/with_auto_deletion.js";
import I18n from "../../base/i18n.js.jsx";
import { compose } from 'redux';

import Button from "../../base/buttons/button.js"
import { ButtonVariantOutlined } from "../buttons/constants.js";
import UploadButton from "../../base/buttons/upload_button.js";
import {
  CircularProgress, FormHelperText
} from '@material-ui/core';

const styles = theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    position: "relative",
  },
  loadingRoot: {
    height: "50px !important"
  },
  title: {
    color: theme.palette.black,
    fontSize: "14px",
    marginBottom: "6px",
    textTransform: "uppercase",
    fontWeight: theme.typography.fontWeightRegular
  },
  subtitle: {
    fontSize: "1rem",
    marginBottom: "10px",
    color: "rgba(0, 0, 0, 0.54)",
  },
  progress: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  },
  fade: {
    opacity: 0.2
  },
  delete: {
    color: theme.palette.redError,
    borderColor: theme.palette.redErrorTransparent
  },
  link: {
    fontSize: "14px",
    textTransform: "uppercase",
    marginBottom: "10px",
    color: "rgba(0, 0, 0, 0.54)",
    wordBreak: "break-word",
    flexGrow: "1"
  }
});

/**
 * Function for upload image to server
 * @function FileField~uploader
 * @param {object} file local picked file
 * @returns {Promise} promise for image uploading
 */

/**
 * Callback for inputs
 * @callback FileField~onChange
 * @param {object} value input's new value
 */

/**
 * Component for input password. Already includes HOCs {@link WithAutoDeletion}, {@link WithHelperText}
 * @name FileField
 * @param {object} props React component properties
 * @param {object} [props.value] Field's value
 * @param {object} [props.file] Local file, picked from file input or external {@link UploadButton}
 * @param {string} [props.string = "*\/*"] Acceptable content type of file
 * @param {FileField~uploader} props.uploader Function for upload file to server
 * @param {boolean} [props.allowDelete = true] Show delete button near uploaded image
 * @param {boolean} [props.allowSelfLoad = true] Show upload button if no one image picked
 * @param {boolean} [props.allowAutoDeleteOnError = false] - Allow or disallow auto deletion
 * @param {number} [props.autoDeleteTimeout = 5] - Duration of auto deletion timer (in seconds)
 * @param {func} props.onAutoDelete - Callback, fires on auto deletion timer is over
 * @param {string} [props.displayName] Field's display name
 * @param {boolean} [props.hasErrors = false] Does errors exists on the field?
 * @param {Array.<string>} [props.errors = []] Error messages array
 * @param {FileField~onChange} [props.onChange] On change handler
 * @example
 * return (
 *   <FileField onChange = { (value) => {
 *                  // Some actions
 *                } }
 *                uploaded = { (file) => {
 *                  return promise;
 *                }}
 *                value = { {id: 1, url: "https://path/to/image.pdf" }} } />
 * )
*/

class FileField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      uploading: false
    };
  }

  onFileChange = (file) => {
    this.setState({ local: file, error: undefined });
    this.upload(file);
  }

  onDeleteClick = (event) => {
    this.setState({ value: undefined, local: undefined, error: undefined });
    this.onChange(undefined);
  }

  onChange = (value) => {
    if (this.props.onChange) {
      this.props.onChange(value);
    }
  }

  upload = (file) => {
    this.setState({ uploading: true, error: undefined, local: undefined  });
    this.props.uploader(file).then((uploadedFile) => {
      this.setState({ uploading: false, value: uploadedFile});
      this.onChange(uploadedFile);
    }).catch((error) => {
      this.setState({ uploading: false,  error: error });
      this.onChange(undefined);
    });
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    let propsValue   = props.value;
    let defaultValue = state.defaultValue;
    let stateValue   = state.value;

    if(JSON.stringify(defaultValue) !== JSON.stringify(propsValue)) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      value = stateValue;
    }

    let hasErrors = Boolean(hasErrors);
    let errorMessages = [];

    if (state.error && state.error.errorData && state.error.errorData.fields) {
      errorMessages = state.error.errorData.fields.attachment || [];
    }

    return {
      ...state,
      hasErrors: hasErrors,
      errorMessages: errorMessages,
      value: value && value.id ? value : undefined,
      local: value && value.file ? value.file : undefined,
      defaultValue: defaultValue
    }
  }

  componentDidMount() {
    const { local } = this.state;
    if(local) {
      this.upload(local);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.state.uploading !== prevState.uploading) {
      this.onChange(this.state.value);
    }
    const { launchCountdown } = this.props;
    if(this.state.error && !prevState.error) {
      launchCountdown();
    }
  }

  render() {
    const { classes, displayName, allowDelete, accept, allowSelfLoad } = this.props;
    const { local, value, uploading, error, hasErrors } = this.state;
    const buttons = [];

    if (allowSelfLoad && !uploading && !local && !value) {
      buttons.push(<UploadButton key = "upload"
                                 multiple = { false }
                                 onChange = { this.onFileChange }
                                 accept = { accept } />);
    }

    if (!uploading && allowDelete && value) {
      buttons.push(
        <Button key = "delete"
                className = { clsx(classes.button, classes.delete) }
                component = "span"
                variant = { ButtonVariantOutlined }
                onClick = { this.onDeleteClick } >
          { I18n.t("action.delete") }
        </Button>
      );
    }
    return <div className = { clsx({
      [classes.root]: true,
      [classes.loadingRoot]: uploading
    }) } >
      {
        displayName &&
        <div className = { classes.title } >
          { displayName }
        </div>
      }
      {
        error &&
        <div className = { classes.subtitle } >
          { error.message }
        </div>
      }
      {
        value &&
        <a href = { value.url }
           target = "_blank"
           className = { classes.link } >
          { value.filename }
        </a>
      }

      {uploading && (
        <div className = { classes.progress } >
          <CircularProgress />
        </div>
      )}
      {error && (
        <div className = { classes.errors } >
          {this.state.errorMessages.map(errorText => (
            <FormHelperText error = { true } key = { errorText } >
              { errorText }
            </FormHelperText>
          ))}
        </div>
      )}

      { buttons.length > 0 && (
        <div className = { classes.buttons } >
          { buttons }
        </div>
      )}
    </div>
  }
}

FileField.propTypes = {
  allowDelete: PropTypes.bool,
  uploader: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  accept: PropTypes.string
};

FileField.defaultProps = {
  allowDelete: true,
  accept: "*/*"
}

export default compose(
  withHelperText,
  withAutoDeletion,
  withStyles(styles, { name: 'FileField' })
)(FileField);
